"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.World = void 0;
const support_1 = require("../support");
const Chunk_1 = require("./Chunk");
const ChunkedPosition_1 = require("./ChunkedPosition");
const ChunkUpdate_1 = require("./ChunkUpdate");
const arrayAsyncHelper_1 = require("../support/arrayAsyncHelper");
const tracingHelper_1 = require("../support/tracingHelper");
let World = (() => {
    class World {
        constructor(chunkSize, updateListener, difficulty, chunkPersistence, tracer) {
            this.chunkSize = chunkSize;
            this.updateListener = updateListener;
            this.difficulty = difficulty;
            this.chunkPersistence = chunkPersistence;
            this.tracer = tracer;
            this.loadedChunks = new Map();
            this.inflightChunkRequests = new Map();
            this.dirtyChunks = new Map();
            setInterval(() => this.saveWorldJob(), 5000);
        }
        async getChunkTiles(context, chunkPos) {
            const span = context.getSpan();
            const chunkOrNull = await this.getChunkOrNull(context, chunkPos);
            span && span.logEvent('chunk-fetched', { chunk: chunkPos.asMapKey() });
            if (chunkOrNull === null) {
                const content = new Array(this.chunkSize.area());
                return content.fill('closed');
            }
            const tiles = chunkOrNull.getActualTiles();
            span && span.logEvent('chunk-tiles', {});
            return await Promise.all(tiles.map((tile, index) => {
                const chunkedPosition = new ChunkedPosition_1.ChunkedPosition(chunkPos, support_1.indexToPos(index, this.chunkSize), this.chunkSize);
                return this.getSingleTileContent(context, tile, chunkedPosition);
            }));
        }
        async openTile(context, chunkedPosition) {
            void (context);
            const tile = await this.getSingleChunkTile(context, chunkedPosition);
            if (tile.isOpen()) {
                await this.tryAutoOpenNeighbor(context, chunkedPosition);
            }
            else {
                tile.open();
                if (tile.isMine()) {
                    this.track(context, -20);
                }
                else {
                    this.track(context, 1);
                }
            }
            const content = await this.getSingleTileContent(context, tile, chunkedPosition);
            if (content === 0) {
                await this.autoOpenZero(context, chunkedPosition);
            }
            await this.markDirty(context, chunkedPosition.getChunk());
            return 0;
        }
        async flag(context, chunkedPosition) {
            const tile = await this.getSingleChunkTile(context, chunkedPosition);
            if (tile.isOpen()) {
                await this.tryAutoFlagNeighbor(context, chunkedPosition);
                return tile.isFlagged();
            }
            else if (tile.isMine()) {
                tile.flag();
                this.track(context, 1);
            }
            else {
                tile.open();
                this.track(context, -20);
                const content = await this.getSingleTileContent(context, tile, chunkedPosition);
                if (content === 0) {
                    await this.autoOpenZero(context, chunkedPosition);
                }
            }
            await this.markDirty(context, chunkedPosition.getChunk());
            return tile.isFlagged();
        }
        async autoOpenZero(context, chunkedPosition) {
            const span = context.getSpan();
            const positions = [
                ...support_1.neighborPositionsChunked(chunkedPosition)
                    .filter(async (pos) => !(await this.getSingleChunkTile(context, pos)).isOpen())
            ];
            const plannedChunkUpdates = new Map();
            while (positions.length !== 0) {
                const position = positions.pop();
                if (position === undefined) {
                    continue;
                }
                const tile = await this.getSingleChunkTile(context, position);
                if (!tile.isOpen()) {
                    tile.open();
                    this.track(context, 1);
                }
                const result = await this.getSingleTileContent(context, tile, position);
                if (result === 0) {
                    positions.push(...await arrayAsyncHelper_1.filterAsync(support_1.neighborPositionsChunked(position), async (pos) => !(await this.getSingleChunkTile(context, pos)).isOpen()));
                }
                plannedChunkUpdates.set(position.getChunk().asMapKey(), position.getChunk());
                span && span.logEvent('opened-tile', {
                    chunk: position.getChunk().asMapKey(),
                    position: position.getPosition().asMapKey()
                });
            }
            for (let [_, chunk] of plannedChunkUpdates.entries()) {
                await this.markDirty(context, chunk);
            }
        }
        async tryAutoOpenNeighbor(context, position) {
            const span = context.getSpan();
            const tile = await this.getSingleChunkTile(context, position);
            const content = await this.getSingleTileContent(context, tile, position);
            const neighborPositions = support_1.neighborPositionsChunked(position);
            const flagCount = (await Promise.all(neighborPositions
                .map(pos => this.getSingleChunkTile(context, pos))))
                .reduce((count, t) => count + (t.isFlagged() || (t.isOpen() && t.isMine()) ? 1 : 0), 0);
            if (content === flagCount) {
                span && span.logEvent('open-neighbors', {});
                const affectedChunks = new Map();
                for (let pos of neighborPositions) {
                    const neighborTile = await this.getSingleChunkTile(context, pos);
                    if (neighborTile.isOpen() || neighborTile.isFlagged()) {
                        continue;
                    }
                    neighborTile.open();
                    this.track(context, 1);
                    const neighborContent = await this.getSingleTileContent(context, neighborTile, pos);
                    if (neighborContent === 0) {
                        span && span.logEvent('open-zero-neighbor', {});
                        await this.autoOpenZero(context, pos);
                    }
                    affectedChunks.set(pos.getChunk().asMapKey(), pos.getChunk());
                }
                for (let [_, chunk] of affectedChunks.entries()) {
                    await this.markDirty(context, chunk);
                }
            }
        }
        async tryAutoFlagNeighbor(context, position) {
            const span = context.getSpan();
            const tile = await this.getSingleChunkTile(context, position);
            const content = await this.getSingleTileContent(context, tile, position);
            const neighborPositions = support_1.neighborPositionsChunked(position);
            const neighborTiles = await Promise.all(neighborPositions
                .map(pos => this.getSingleChunkTile(context, pos)));
            span && span.logEvent('neighborhood-tiles', {
                positions: neighborPositions.map(pos => ({
                    chunk: pos.getChunk().asMapKey(),
                    position: pos.getPosition().asMapKey()
                }))
            });
            const unopenedOrMineOrFlaggedCount = neighborTiles
                .reduce((count, t) => count + ((!t.isOpen() || t.isMine() || t.isFlagged()) ? 1 : 0), 0);
            if (content === unopenedOrMineOrFlaggedCount) {
                const affectedChunks = new Map();
                for (let i = 0; i < neighborPositions.length; i++) {
                    const neighborTile = neighborTiles[i];
                    if (neighborTile.isOpen() || neighborTile.isFlagged()) {
                        continue;
                    }
                    neighborTile.flag();
                    this.track(context, 1);
                    const chunk = neighborPositions[i].getChunk();
                    affectedChunks.set(chunk.asMapKey(), chunk);
                }
                span && span.logEvent('affected-chunk-count', affectedChunks.size);
                for (let [_, chunk] of affectedChunks.entries()) {
                    span && span.logEvent('chunk-mark-dirty', { chunk: chunk.asMapKey() });
                    await this.markDirty(context, chunk);
                }
            }
        }
        generateChunk(chunkPos) {
            console.log('[World] Generating chunk', chunkPos);
            if (this.loadedChunks.has(chunkPos.asMapKey())) {
                throw new Error("Tried to generate chunk that already exists");
            }
            const chunk = Chunk_1.Chunk.generate(chunkPos, this.chunkSize, this.difficulty);
            this.loadedChunks.set(chunkPos.asMapKey(), chunk);
            return chunk;
        }
        async getChunk(context, chunkPos) {
            return (await this.getChunkOrNull(context, chunkPos, true));
        }
        async getChunkOrNull(_, chunkPos, shouldGenerate = false) {
            const chunkKey = chunkPos.asMapKey();
            if (this.loadedChunks.has(chunkPos.asMapKey())) {
                return this.loadedChunks.get(chunkPos.asMapKey());
            }
            let persistedChunkPromise;
            if (this.inflightChunkRequests.has(chunkKey)) {
                console.log('getting chunk that has an inflight request');
                persistedChunkPromise = this.inflightChunkRequests.get(chunkKey);
            }
            else {
                persistedChunkPromise = this.chunkPersistence.loadChunk(chunkPos);
                this.inflightChunkRequests.set(chunkKey, persistedChunkPromise);
            }
            const persistedChunk = await persistedChunkPromise;
            this.inflightChunkRequests.delete(chunkKey);
            if (this.loadedChunks.has(chunkKey)) {
                return this.loadedChunks.get(chunkKey);
            }
            if (persistedChunk === null) {
                if (shouldGenerate) {
                    const generated = this.generateChunk(chunkPos);
                    this.loadedChunks.set(chunkKey, generated);
                    return generated;
                }
                return null;
            }
            const chunk = Chunk_1.Chunk.load(chunkPos, this.chunkSize, persistedChunk);
            this.loadedChunks.set(chunkKey, chunk);
            return chunk;
        }
        async getNeighbors(context, chunkedPosition) {
            const neighborPositions = support_1.neighborPositionsChunked(chunkedPosition);
            return await Promise.all(neighborPositions
                .map(pos => this.getSingleChunkTile(context, pos)));
        }
        async getSingleChunkTile(context, pos) {
            return (await this.getChunk(context, pos.getChunk())).getTile(pos.getPosition());
        }
        async getSingleTileContent(context, tile, chunkedPosition) {
            if (tile.hasCachedContent() !== undefined) {
                return tile.hasCachedContent();
            }
            if (tile.isFlagged()) {
                return 'flag';
            }
            if (!tile.isOpen()) {
                return 'closed';
            }
            if (tile.isMine()) {
                return 'mine';
            }
            return tile.calculateNumber(await this.getNeighbors(context, chunkedPosition));
        }
        async markDirty(context, chunkPos) {
            this.dirtyChunks.set(chunkPos.asMapKey(), chunkPos);
            this.updateListener(new ChunkUpdate_1.ChunkUpdate(chunkPos, await this.getChunkTiles(context, chunkPos), this.chunkSize));
        }
        async saveWorldJob() {
            if (this.dirtyChunks.size === 0) {
                return;
            }
            console.log('[World] Saving world');
            await Promise.all([...this.dirtyChunks.values()]
                .filter(pos => this.loadedChunks.has(pos.asMapKey()))
                .map(pos => this.loadedChunks.get(pos.asMapKey()))
                .map(chunk => this.chunkPersistence.persistChunk(chunk.toPersistent())));
            console.log('[World] World save successful, saved ', this.dirtyChunks.size, 'dirty chunks');
            this.dirtyChunks.clear();
        }
        track(ctx, points) {
            const fn = ctx.getData('track');
            if (fn) {
                fn(points);
            }
        }
        getTracer() {
            return this.tracer;
        }
    }
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, support_1.Vector2]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "getChunkTiles", null);
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, ChunkedPosition_1.ChunkedPosition]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "openTile", null);
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, ChunkedPosition_1.ChunkedPosition]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "flag", null);
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, ChunkedPosition_1.ChunkedPosition]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "autoOpenZero", null);
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, ChunkedPosition_1.ChunkedPosition]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "tryAutoOpenNeighbor", null);
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, ChunkedPosition_1.ChunkedPosition]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "tryAutoFlagNeighbor", null);
    __decorate([
        tracingHelper_1.traced(),
        __metadata("design:type", Function),
        __metadata("design:paramtypes", [support_1.Context, support_1.Vector2]),
        __metadata("design:returntype", Promise)
    ], World.prototype, "markDirty", null);
    return World;
})();
exports.World = World;
//# sourceMappingURL=World.js.map